/**
 * Personal tab — MEMBER-self-serve edits for the user's small Tier-B-user
 * fields. Per the canonical guide §1.1 + handoff §3, this surface covers:
 *   - `personaSuffix`  — short free-text appended to the agent persona.
 *   - `locale`         — sourced from the existing Language section; this
 *                        tab links to it (no duplicate widget).
 *   - `agents.main.{name,avatarUrl}` — per-user agent identity override.
 *
 * Slice 1 implements **personaSuffix only** as the canonical pattern (the
 * smallest viable PATCH path that exercises: read → form → kill-switch
 * gating → write → refresh shared context). Subsequent slices fold in the
 * agent-identity override.
 *
 * Key UX rules (handoff §6 + canonical guide §2):
 *   - The control is **disabled when `org.userSettingsPolicy.personaSuffix`
 *     is `false`** AND a tooltip surfaces *why* (no silent gating).
 *   - Successful PATCH triggers `refresh()` on the shared context so any
 *     `disabledByOrg` changes propagate to sibling tabs.
 *   - 409 USER_SETTING_DISABLED_BY_ORG races (org flipped the switch
 *     between focus and submit) flip the UI to disabled with a toast.
 *   - 2 KB body cap is enforced server-side; we surface the count + soft
 *     cap at 2000 chars (one rune budget) to spare the user a 400.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Lock, Save, User as UserIcon, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { useToastStore } from "@/stores/useToastStore";
import {
  patchOpenclawUserConfig,
  LxOpenclawConfigError,
} from "@/api/lx-openclaw-config";
import { isDisabledByOrg } from "@/lib/openclaw-policy";
import { cn } from "@/lib/utils";
import { useOpenclawSettings } from "./OpenclawSettingsTabs";

const PERSONA_SUFFIX_SOFT_CAP = 2000;

export function PersonalTab() {
  const { t } = useTranslation();
  const { config, refresh, notifyRouteNotFound } = useOpenclawSettings();
  const addToast = useToastStore((s) => s.addToast);

  const personaDisabled = isDisabledByOrg(config.org, "personaSuffix");
  const serverValue = config.user?.personaSuffix ?? "";

  // Local edit buffer. Reset whenever the canonical server value flips
  // (e.g. after a successful save, or after `refresh()` from another
  // sibling tab) so the form stays in sync without trapping dirty state.
  const [draft, setDraft] = useState(serverValue);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    setDraft(serverValue);
  }, [serverValue]);

  const dirty = draft !== serverValue;
  const overSoftCap = draft.length > PERSONA_SUFFIX_SOFT_CAP;
  const canSave = dirty && !personaDisabled && !submitting && !overSoftCap;

  async function onSave() {
    if (!canSave) return;
    setSubmitting(true);
    try {
      await patchOpenclawUserConfig({ personaSuffix: draft });
      // Sync siblings — they may render a "disabledByOrg" badge that
      // depends on the most-recent policy view, and our PATCH could
      // have raced with a policy flip.
      await refresh();
      addToast({
        kind: "info",
        text: t("settings.openclaw.personal.saved"),
        autoDismissMs: 4000,
      });
    } catch (err) {
      handleSaveError(err);
    } finally {
      setSubmitting(false);
    }
  }

  function handleSaveError(err: unknown) {
    if (err instanceof LxOpenclawConfigError) {
      if (err.routeNotFound) {
        notifyRouteNotFound("personal");
        addToast({
          kind: "error",
          text: t("settings.openclaw.upgradeRequired.toast"),
          autoDismissMs: 8000,
        });
        return;
      }
      if (err.code === "USER_SETTING_DISABLED_BY_ORG") {
        // Race — org flipped the switch between focus and submit. Pull
        // the latest config so the UI reflects the locked state on the
        // very next render.
        void refresh();
        addToast({
          kind: "error",
          text: t("settings.openclaw.disabledByOrgRace"),
          autoDismissMs: 6000,
        });
        return;
      }
      if (err.code === "VALIDATION_ERROR") {
        addToast({
          kind: "error",
          text: t("settings.openclaw.personal.tooLong"),
          autoDismissMs: 6000,
        });
        return;
      }
    }
    addToast({
      kind: "error",
      text: t("settings.openclaw.saveFailed"),
      autoDismissMs: 6000,
    });
  }

  return (
    <Card variant="surface" data-testid="openclaw-personal-tab">
      <div className="flex items-center gap-2">
        <UserIcon size={18} className="text-cream-500" />
        <h3 className="font-serif-display text-display-sm text-coffee-900 dark:text-cream-100">
          {t("settings.openclaw.personal.title")}
        </h3>
      </div>
      <p className="mt-3 text-body-sm text-coffee-600 dark:text-cream-400">
        {t("settings.openclaw.personal.caption")}
      </p>

      <div className="mt-5 flex flex-col gap-2">
        <label
          htmlFor="openclaw-persona-suffix"
          className="flex items-center gap-2 text-body-sm font-medium text-coffee-700 dark:text-cream-300"
        >
          {t("settings.openclaw.personal.personaSuffixLabel")}
          {personaDisabled && (
            <span
              data-testid="openclaw-personal-persona-locked"
              title={t("settings.openclaw.lockedByOrgTooltip")}
              className="inline-flex items-center gap-1 rounded bg-cream-100 px-1.5 py-0.5 text-[10px] font-medium text-coffee-500 dark:bg-coffee-800 dark:text-cream-400"
            >
              <Lock size={9} />
              {t("settings.openclaw.lockedByOrg")}
            </span>
          )}
        </label>
        <textarea
          id="openclaw-persona-suffix"
          data-testid="openclaw-personal-persona-input"
          value={draft}
          disabled={personaDisabled || submitting}
          rows={4}
          maxLength={PERSONA_SUFFIX_SOFT_CAP + 200}
          placeholder={t("settings.openclaw.personal.personaSuffixPlaceholder")}
          onChange={(e) => setDraft(e.target.value)}
          className={cn(
            "w-full resize-y rounded-md border border-cream-200/80 bg-cream-50 px-3 py-2 text-body-sm text-coffee-900 outline-none transition-colors",
            "focus-visible:border-gold-500 focus-visible:ring-2 focus-visible:ring-gold-500/30",
            "dark:border-coffee-700/80 dark:bg-coffee-900 dark:text-cream-100",
            "disabled:cursor-not-allowed disabled:opacity-60",
          )}
        />
        <div className="flex items-center justify-between gap-2">
          <p className="text-caption text-coffee-500 dark:text-cream-500">
            {t("settings.openclaw.personal.personaSuffixHint")}
          </p>
          <span
            data-testid="openclaw-personal-persona-count"
            className={cn(
              "text-caption",
              overSoftCap
                ? "text-red-600 dark:text-red-400"
                : "text-coffee-500 dark:text-cream-500",
            )}
          >
            {draft.length}/{PERSONA_SUFFIX_SOFT_CAP}
          </span>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-end gap-2">
        <button
          type="button"
          data-testid="openclaw-personal-save"
          disabled={!canSave}
          onClick={() => void onSave()}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-body-sm font-medium transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 dark:focus-visible:ring-offset-coffee-900",
            canSave
              ? "border-coffee-800 bg-coffee-800 text-cream-50 hover:bg-coffee-900 hover:border-coffee-900 dark:border-cream-100 dark:bg-cream-100 dark:text-coffee-900 dark:hover:bg-cream-50 dark:hover:border-cream-50"
              : "cursor-not-allowed border-cream-200/60 text-cream-500 dark:border-coffee-700/60",
          )}
        >
          {submitting ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Save size={13} />
          )}
          {submitting
            ? t("settings.openclaw.savingButton")
            : t("settings.openclaw.saveButton")}
        </button>
      </div>
    </Card>
  );
}
